/**
 * vortexPipeline — radial spiral converging on a central singularity.
 *
 * Same volumetric raymarcher foundation as Beat Nebula, but rendered
 * as a polar-coordinate spiral: rays step outward from the center,
 * each ray records how many "filaments" it crosses, and we accumulate
 * their colors with tanh tone mapping.
 *
 * Visual: an inverted whirlpool of light. Multiple spiral arms in
 * cyan/violet/gold, all rotating around a bright core.
 *
 * Audio drives:
 *   - bass: rotation speed + spiral tightness
 *   - mid:  filament brightness
 *   - high: core brightness + sparkle bursts
 *
 * No mesh, no simulation — single fullscreen quad fragment shader.
 */
import { linkProgram, POST_ATTRIBS } from "./glContext";

export interface VortexPipeline {
  program: WebGLProgram;
  vao: WebGLVertexArrayObject | null;
  vbo: WebGLBuffer | null;
  uResolution: WebGLUniformLocation | null;
  uTime: WebGLUniformLocation | null;
  uBass: WebGLUniformLocation | null;
  uMid: WebGLUniformLocation | null;
  uHigh: WebGLUniformLocation | null;
  bands: Float32Array;
}

const VERT_SRC = `#version 300 es
precision highp float;
layout(location = 0) in vec2 aPos;
out vec2 vUV;
void main() {
  vUV = aPos * 0.5 + 0.5;
  gl_Position = vec4(aPos, 0.0, 1.0);
}`;

const FRAG_SRC = `#version 300 es
precision highp float;
in vec2 vUV;
out vec4 outColor;

uniform vec2 uResolution;
uniform float uTime;
uniform vec3 uBass;
uniform vec3 uMid;
uniform vec3 uHigh;

float hash21(vec2 p) {
  p = fract(p * vec2(123.34, 456.21));
  p += dot(p, p + 45.32);
  return fract(p.x * p.y);
}

float valueNoise(vec2 p) {
  vec2 i = floor(p);
  vec2 f = fract(p);
  f = f * f * (3.0 - 2.0 * f);
  float a = hash21(i);
  float b = hash21(i + vec2(1.0, 0.0));
  float c = hash21(i + vec2(0.0, 1.0));
  float d = hash21(i + vec2(1.0, 1.0));
  return mix(mix(a, b, f.x), mix(c, d, f.x), f.y);
}

void main() {
  float bass = (uBass.x + uBass.y + uBass.z) / 3.0;
  float mid = (uMid.x + uMid.y + uMid.z) / 3.0;
  float high = (uHigh.x + uHigh.y + uHigh.z) / 3.0;
  bass = pow(clamp(bass * 2.5, 0.0, 1.0), 0.7);
  mid = pow(clamp(mid * 3.0, 0.0, 1.0), 0.7);
  high = pow(clamp(high * 4.0, 0.0, 1.0), 0.7);

  // Polar coords from center.
  vec2 p = (gl_FragCoord.xy - uResolution * 0.5) / uResolution.y;
  float r = length(p);
  float theta = atan(p.y, p.x);

  // Background: dark with subtle radial gradient.
  vec3 bg = mix(
    vec3(0.04, 0.06, 0.14),  // center: deep teal/navy
    vec3(0.01, 0.01, 0.04),  // edges: very dark
    smoothstep(0.0, 0.6, r)
  );

  // Build the vortex by sampling polar coords with a spiral twist.
  // For each pixel at (r, theta), we evaluate a few spiral arms
  // — bands that rotate with theta.
  float t = uTime;
  float twist = 0.05 + 0.04 * sin(t * 0.3) + bass * 0.06;

  vec3 col = bg;

  // Number of spiral arms.
  const int ARMS = 5;
  for (int i = 0; i < ARMS; i++) {
    float armId = float(i);
    // Each arm has a phase offset.
    float phase = armId * 6.2831853 / float(ARMS);
    // The arm is at theta + twist * r - phase = a function of r.
    // As r increases, the spiral wraps around.
    float armAngle = theta + twist * r * 6.0 - t * (0.5 + armId * 0.1) - phase;
    // Distance to the nearest arm center.
    float armDist = abs(fract(armAngle / 6.2831853 + 0.5) - 0.5) * 6.2831853;

    // Spiral falls off with radius (brighter near the center).
    float radius = smoothstep(0.55, 0.05, r);
    float arm = exp(-armDist * armDist * 8.0) * radius;

    // Per-arm color (rainbow spiral).
    float hue = armId / float(ARMS);
    vec3 armColor = vec3(
      0.5 + 0.5 * sin(hue * 6.2831853 + 0.0),
      0.5 + 0.5 * sin(hue * 6.2831853 + 2.094),
      0.5 + 0.5 * sin(hue * 6.2831853 + 4.189)
    );
    // Mix toward cyan/purple palette.
    vec3 coolColor = mix(
      vec3(0.1, 0.6, 0.95),  // cyan
      vec3(0.6, 0.2, 0.95),  // violet
      armId / float(ARMS - 1)
    );
    armColor = mix(armColor, coolColor, 0.7);

    // Audio modulation per arm.
    float armAudio = 0.6 + 0.5 * mid;
    col += armColor * arm * armAudio * (1.0 + bass * 1.5);
  }

  // Bright central core.
  float core = smoothstep(0.08, 0.0, r);
  vec3 coreColor = vec3(1.0, 0.95, 0.85);  // warm white
  col += coreColor * core * (0.8 + high * 1.5);

  // Particle sparkles around the core.
  float spark = step(0.95, valueNoise(p * 30.0 + vec2(t * 0.5, 0.0)));
  float sparkMask = smoothstep(0.3, 0.05, r);
  col += vec3(1.0, 0.9, 0.7) * spark * sparkMask * high * 0.6;

  // Gravitational lensing distortion: tiny radial ripples.
  float ripple = sin(r * 30.0 - t * 4.0) * 0.005;
  col += vec3(0.3, 0.5, 0.8) * smoothstep(0.05, 0.0, r) * ripple * 30.0 * 0.5;

  // Tone mapping (ACES-approximation).
  col = (col * (2.51 * col + 0.03)) / (col * (2.43 * col + 0.59) + 0.14);
  col = pow(clamp(col, 0.0, 1.0), vec3(1.0 / 2.2));

  // Vignette to keep edges dark.
  float vig = 1.0 - smoothstep(0.3, 0.7, r) * 0.5;
  col *= vig;

  outColor = vec4(col, 1.0);
}
`;

export function computeVortexBands(t: number, out: Float32Array): void {
  // Slightly faster than aurora, more rhythmic.
  out[0] = 0.5 + 0.3 * Math.sin(t * 0.6);
  out[1] = 0.5 + 0.3 * Math.sin(t * 0.9 + 1.0);
  out[2] = 0.4 + 0.3 * Math.sin(t * 1.2 + 2.0);
  out[3] = 0.5 + 0.3 * Math.sin(t * 1.4 + 1.5);
  out[4] = 0.5 + 0.3 * Math.sin(t * 1.7 + 0.5);
  out[5] = 0.4 + 0.3 * Math.sin(t * 2.0 + 2.5);
  out[6] = 0.3 + 0.4 * Math.sin(t * 2.5 + 0.7);
  out[7] = 0.4 + 0.3 * Math.sin(t * 3.0 + 1.8);
  out[8] = 0.3 + 0.4 * Math.sin(t * 3.5 + 3.0);
}

export function computeVortexRealtimeBands(
  freqData: Uint8Array,
  out: Float32Array,
): void {
  const N = freqData.length;
  const ranges = [
    [0.00, 0.030],
    [0.030, 0.080],
    [0.080, 0.150],
    [0.150, 0.260],
    [0.260, 0.380],
    [0.380, 0.500],
    [0.500, 0.660],
    [0.660, 0.820],
    [0.820, 0.980],
  ];
  for (let i = 0; i < 9; i++) {
    const [lo, hi] = ranges[i];
    const start = Math.floor(lo * N);
    const end = Math.floor(hi * N);
    let sum = 0, count = 0;
    for (let k = start; k < end; k++) {
      sum += freqData[k];
      count++;
    }
    out[i] = count > 0 ? (sum / count) / 255 : 0;
  }
}

export function createVortexPipeline(gl: WebGL2RenderingContext): VortexPipeline | null {
  const program = linkProgram(gl, VERT_SRC, FRAG_SRC, POST_ATTRIBS);
  if (!program) return null;
  const vao = gl.createVertexArray();
  const vbo = gl.createBuffer();
  if (!vao || !vbo) return null;
  const verts = new Float32Array([
    -1, -1,  1, -1,  -1, 1,
    -1,  1,  1, -1,   1, 1,
  ]);
  gl.bindVertexArray(vao);
  gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
  gl.bufferData(gl.ARRAY_BUFFER, verts, gl.STATIC_DRAW);
  gl.enableVertexAttribArray(POST_ATTRIBS.aPos);
  gl.vertexAttribPointer(POST_ATTRIBS.aPos, 2, gl.FLOAT, false, 0, 0);
  gl.bindVertexArray(null);
  return {
    program, vao, vbo,
    uResolution: gl.getUniformLocation(program, "uResolution"),
    uTime: gl.getUniformLocation(program, "uTime"),
    uBass: gl.getUniformLocation(program, "uBass"),
    uMid: gl.getUniformLocation(program, "uMid"),
    uHigh: gl.getUniformLocation(program, "uHigh"),
    bands: new Float32Array(9),
  };
}

export function drawVortex(
  gl: WebGL2RenderingContext,
  pipe: VortexPipeline,
  width: number,
  height: number,
  t: number,
): void {
  gl.useProgram(pipe.program);
  const prog = pipe.program;
  gl.uniform2f(gl.getUniformLocation(prog, "uResolution"), width, height);
  gl.uniform1f(gl.getUniformLocation(prog, "uTime"), t);
  gl.uniform3f(gl.getUniformLocation(prog, "uBass"), pipe.bands[0], pipe.bands[1], pipe.bands[2]);
  gl.uniform3f(gl.getUniformLocation(prog, "uMid"), pipe.bands[3], pipe.bands[4], pipe.bands[5]);
  gl.uniform3f(gl.getUniformLocation(prog, "uHigh"), pipe.bands[6], pipe.bands[7], pipe.bands[8]);
  gl.bindVertexArray(pipe.vao);
  gl.drawArrays(gl.TRIANGLES, 0, 6);
  gl.bindVertexArray(null);
}
